import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, GraduationCap, Info } from 'lucide-react';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { useUpsertOpportunite } from '@/hooks/queries/useCrmOpportunites';
import { useFormations } from '@/hooks/queries/useFormation';
import { supabase } from '@/lib/supabase';
import { formatDA } from '@/lib/format';
import type { OpportuniteRow } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  opportunite: OpportuniteRow;
}

const TVA_DZ_RATE = 0.19;

// Sprint 6.9.11 — La modal écrit désormais dans recouvrement_entreprises (Sprint 6.9.3)
// au lieu de echeances (Sprint 3). Source de vérité unique pour les créances B2B,
// cohérent avec le widget Trésorerie prévisionnelle Sprint 6.9.9.
//
// Pourquoi ? Avant Sprint 6.9.11 :
//   ConversionModal créait dans echeances → invisible côté Recouvrement Entreprises
//   → invisible côté Trésorerie Dashboard. Doublon de truth (cf. audit Sprint 6.9.10).
//
// Le module echeances reste autonome pour les autres usages (saisie manuelle,
// relances, plans_paiement) — il n'est plus alimenté par le CRM.

export function ConversionModal({ open, onOpenChange, opportunite }: Props): JSX.Element {
  const upsertOpp = useUpsertOpportunite();

  const today = new Date().toISOString().slice(0, 10);
  const echeance30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const [factureRef, setFactureRef] = useState('');
  const [dateEcheance, setDateEcheance] = useState(echeance30);
  const [appliqueTva, setAppliqueTva] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprint 6.9.12 — section "Créer aussi en Formation" (optionnelle)
  const [createSession, setCreateSession] = useState(false);
  const [formationId, setFormationId] = useState('');
  const [typeSession, setTypeSession] = useState<'intra' | 'inter'>('intra');
  const [dateSession, setDateSession] = useState(today);
  const { data: formations = [] } = useFormations({ entrepriseId: opportunite.entreprise_id });

  useEffect(() => {
    if (!open) return;
    setFactureRef(`FACT-${new Date().getFullYear()}-${opportunite.reference.replace('OPP-', '')}`);
    setDateEcheance(echeance30);
    setAppliqueTva(true);
    setError(null);
    setCreateSession(false);
    setFormationId('');
    setTypeSession('intra');
    setDateSession(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunite.reference]);

  // facture_generee_id existe sur opportunites (cf. audit Sprint 6.9.10).
  const alreadyConverted =
    !!(opportunite as unknown as { facture_generee_id?: string | null }).facture_generee_id ||
    !!opportunite.echeance_generee_id;

  const ht = opportunite.montant_estime;
  const tva = appliqueTva ? Math.round(ht * TVA_DZ_RATE * 100) / 100 : 0;
  const ttc = Math.round((ht + tva) * 100) / 100;

  const handleConvert = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (alreadyConverted) {
      setError('Cette opportunité a déjà été convertie.');
      return;
    }
    if (!opportunite.tier_id) {
      setError("Tier obligatoire pour convertir. Modifiez l'opportunité d'abord.");
      return;
    }
    if (opportunite.montant_estime <= 0) {
      setError('Montant > 0 obligatoire pour convertir.');
      return;
    }
    if (!factureRef.trim()) {
      setError('Référence facture obligatoire.');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Récupère le nom du tier (NOT NULL sur recouvrement_entreprises.entreprise_cliente_nom)
      const { data: tierRow, error: errTier } = await supabase
        .from('tiers')
        .select('raison_sociale')
        .eq('id', opportunite.tier_id)
        .maybeSingle();
      if (errTier) throw errTier;
      const clientNom =
        (tierRow as { raison_sociale: string } | null)?.raison_sociale ?? opportunite.titre;

      // 2. INSERT recouvrement_entreprises
      const observation = [
        `Issue de l'opportunité ${opportunite.reference}`,
        `Échéance prévue : ${dateEcheance}`,
        opportunite.description ?? null,
      ]
        .filter(Boolean)
        .join(' · ');

      const { data: rec, error: errRec } = await supabase
        .from('recouvrement_entreprises')
        .insert({
          entreprise_id: opportunite.entreprise_id,
          activite_id: opportunite.activite_id,
          tier_id: opportunite.tier_id,
          entreprise_cliente_nom: clientNom,
          annee: new Date().getFullYear(),
          reference_facture: factureRef.trim(),
          formation_id: null,
          formation_titre: null,
          date_formation: today,
          lieu: null,
          participants: 0,
          montant_ht: ht,
          montant_tva: tva,
          montant_ttc: ttc,
          montant_encaisse: 0,
          statut: 'a_encaisser',
          commercial_id: opportunite.commercial_id,
          observation: observation.slice(0, 1000),
        } as never)
        .select('id, reference_facture')
        .single();
      if (errRec) throw errRec;
      const factureRow = rec as { id: string; reference_facture: string } | null;
      if (!factureRow) throw new Error('Insert sans retour');

      // 3. Sprint 6.9.15a — Déplace l'opportunité vers l'étape 'won' du pipeline
      // (avant : on laissait l'opp sur son étape actuelle, l'utilisateur devait drag&drop).
      // Si le pipeline n'a aucune étape 'won', on ne touche pas etape_id (cas legacy).
      const { data: wonEtape } = await supabase
        .from('pipeline_etapes')
        .select('id')
        .eq('pipeline_id', opportunite.pipeline_id)
        .eq('type', 'won')
        .order('ordre', { ascending: true })
        .limit(1)
        .maybeSingle();
      const targetEtapeId = (wonEtape as { id: string } | null)?.id ?? opportunite.etape_id;

      await upsertOpp.mutateAsync({
        id: opportunite.id,
        pipeline_id: opportunite.pipeline_id,
        etape_id: targetEtapeId,
        entreprise_id: opportunite.entreprise_id,
        titre: opportunite.titre,
      });
      const { error: errPatch } = await supabase
        .from('opportunites')
        .update({ facture_generee_id: factureRow.id } as never)
        .eq('id', opportunite.id);
      if (errPatch) throw errPatch;

      // 4. Sprint 6.9.12 — Si demandé, créer aussi la session + inscription côté Formation
      let sessionMsg = '';
      if (createSession && formationId && opportunite.tier_id) {
        // Cherche d'abord une session existante (formation, date, type) pour rattachement
        // (cas INTER : N opps sur la même session). Pour INTRA, c'est moins courant mais cohérent.
        const { data: existingSession } = await supabase
          .from('sessions_formation')
          .select('id, reference')
          .eq('formation_id', formationId)
          .eq('date_debut', dateSession)
          .eq('type', typeSession)
          .limit(1)
          .maybeSingle();

        let sessionId: string;
        let sessionRef: string;
        const existingTyped = existingSession as { id: string; reference: string | null } | null;
        if (existingTyped) {
          sessionId = existingTyped.id;
          sessionRef = existingTyped.reference ?? sessionId.slice(0, 8);
        } else {
          const refAuto = `SES-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`;
          const { data: createdSession, error: errSession } = await supabase
            .from('sessions_formation')
            .insert({
              formation_id: formationId,
              reference: refAuto,
              date_debut: dateSession,
              date_fin: dateSession,
              type: typeSession,
              statut: 'planifiee',
            } as never)
            .select('id, reference')
            .single();
          if (errSession) throw new Error(`Session KO : ${errSession.message}`);
          const createdTyped = createdSession as { id: string; reference: string };
          sessionId = createdTyped.id;
          sessionRef = createdTyped.reference;
        }

        // Inscription liée à l'opportunité (cross-ref CRM ↔ Formation)
        const { error: errInscription } = await supabase.from('inscriptions_formation').insert({
          session_id: sessionId,
          tier_id: opportunite.tier_id,
          prix_facture: ht,
          opportunite_id: opportunite.id,
          statut: 'inscrit',
        } as never);
        if (errInscription) {
          // L'inscription a peut-être échoué pour cause de doublon ou contrainte ; on continue
          // mais on signale. Pas bloquant pour la facture.
          sessionMsg = ` · ⚠ Session OK mais inscription KO : ${errInscription.message}`;
        } else {
          sessionMsg = ` · Session ${sessionRef} (${typeSession}) + inscription liée`;
        }
      }

      toast.success(
        `Facture ${factureRow.reference_facture} créée dans Recouvrement — TTC ${formatDA(ttc)}${sessionMsg}`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Convertir l'opportunité en facture"
      description={
        alreadyConverted
          ? 'Cette opportunité a déjà été convertie. Action impossible.'
          : "Génère une facture dans le module Recouvrement Entreprises avec le montant de l'opportunité."
      }
      onSubmit={handleConvert}
      isSubmitting={submitting}
      submitDisabled={alreadyConverted}
      submitLabel="Créer la facture"
      size="md"
    >
      {alreadyConverted && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          Cette opportunité a déjà une facture/échéance générée — pour préserver l&apos;intégrité
          comptable, la conversion n&apos;est pas répétable.
        </div>
      )}
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          La facture sera créée dans <strong>Recouvrement Entreprises</strong> (cohérent avec le
          widget Trésorerie Dashboard). Les relances et plans de paiement restent gérables depuis le
          module Échéances séparément.
        </span>
      </div>
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <div className="space-y-1 rounded-md border border-border bg-muted/30 p-3 text-xs">
        <div>
          <span className="text-muted-foreground">Opportunité : </span>
          <span className="font-mono">{opportunite.reference}</span> — {opportunite.titre}
        </div>
        <div>
          <span className="text-muted-foreground">HT : </span>
          <span className="num font-semibold">
            {formatDA(ht)} {opportunite.devise}
          </span>
          {appliqueTva && (
            <>
              <span className="ml-2 text-muted-foreground">TVA 19 % : </span>
              <span className="num">{formatDA(tva)}</span>
              <span className="ml-2 text-muted-foreground">TTC : </span>
              <span className="num font-semibold">{formatDA(ttc)}</span>
            </>
          )}
        </div>
      </div>
      <FormField
        label="Référence facture"
        value={factureRef}
        onChange={(e) => setFactureRef(e.target.value)}
        required
      />
      <FormField
        label="Date d'échéance prévue"
        type="date"
        value={dateEcheance}
        onChange={(e) => setDateEcheance(e.target.value)}
        required
        hint="Stockée dans l'observation (la table recouvrement n'a pas de date_echeance dédiée)"
      />
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={appliqueTva}
          onChange={(e) => setAppliqueTva(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        <span>
          Appliquer TVA 19 % algérienne (recalcul live ci-dessus). Décocher si le montant
          opportunité est déjà TTC.
        </span>
      </label>

      <div className="rounded-md border-2 border-violet-200 bg-violet-50/50 p-3">
        <label className="flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={createSession}
            onChange={(e) => setCreateSession(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5"
            disabled={alreadyConverted}
          />
          <span>
            <strong className="inline-flex items-center gap-1 text-violet-900">
              <GraduationCap className="h-3 w-3" /> Créer aussi en Formation
            </strong>
            <span className="ml-1 text-muted-foreground">
              — génère session + inscription liée à cette opportunité (cross-ref CRM ↔ Formation)
            </span>
          </span>
        </label>
        {createSession && (
          <div className="mt-3 space-y-3 border-t border-violet-200 pt-3">
            <FormSelect
              label="Formation cible"
              value={formationId}
              onChange={setFormationId}
              options={[
                { value: '', label: '— Sélectionner une formation —' },
                ...formations.map((f) => ({
                  value: f.id,
                  label: `${f.code} · ${f.titre}`,
                })),
              ]}
              required
              hint={
                formations.length === 0
                  ? 'Aucune formation dans le catalogue de cette entreprise. Créez-la dans Formation > Catalogue.'
                  : undefined
              }
            />
            <div>
              <label className="text-xs font-semibold text-foreground">Type de session</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTypeSession('intra')}
                  className={`rounded-md border px-2 py-2 text-left text-xs transition ${
                    typeSession === 'intra'
                      ? 'border-violet-500 bg-violet-100 ring-1 ring-violet-400'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="block font-semibold">🏢 Intra-entreprise</span>
                  <span className="block text-[11px] text-muted-foreground">
                    1 client = 1 session dédiée
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTypeSession('inter')}
                  className={`rounded-md border px-2 py-2 text-left text-xs transition ${
                    typeSession === 'inter'
                      ? 'border-violet-500 bg-violet-100 ring-1 ring-violet-400'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="block font-semibold">🤝 Interentreprise</span>
                  <span className="block text-[11px] text-muted-foreground">
                    N clients sur la même session
                  </span>
                </button>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {typeSession === 'inter'
                  ? "Si une session inter existe déjà à cette date, l'inscription y sera rattachée."
                  : 'Une nouvelle session sera créée pour ce client uniquement.'}
              </p>
            </div>
            <FormField
              label="Date session"
              type="date"
              value={dateSession}
              onChange={(e) => setDateSession(e.target.value)}
              required
            />
          </div>
        )}
      </div>
    </FormDialog>
  );
}
